import {Decoder} from 'json-bouncer'
import * as R from 'ramda'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import {Dictionary} from '../../Data/Dictionary'
import {
    mandatoryStringValidator,
    stringToIntValidator,
    validateInputField
} from '../../Data/FormValidation'
import {buildUrl, HttpError} from '../../Data/Http'
import {HttpRequest} from '../../Data/HttpRequest'
import {update} from '../../Data/ObjectHelper'
import * as PageSection from '../../Data/PageSection'
import * as Paginated from '../../Data/Paginated'
import * as Pagination from '../../Data/Pagination'
import * as Result from '../../Data/Result'
import {navigate} from '../../Data/Browser'
import {Session} from '../../Data/Session'
import * as InquiryForList from '../../Entities/InquiryForList'
import * as InquiryResource from '../../Request/InquiryResource'
import {InquiryOverviewAction} from './InquiryOverviewActions'
import {
    InquiryOverviewFeedback,
    InquiryOverviewRequestIdentifier
} from './InquiryOverviewFeedback'
import {InquiryOverviewState} from './InquiryOverviewState'

const initialItemsPerPage = 10

export function activate(
    session: Session,
    queryParameters: Dictionary<string>,
): [HttpRequest, PageSection.M] {
    const pageSection =
        PageSection.fromQueryParamters(initialItemsPerPage, queryParameters)
    return [
        InquiryResource.list(
            session.manufacturer.id,
            PageSection.toOffsetLimit(pageSection),
        ),
        pageSection,
    ]
}

export function initialDecoder(): Decoder<Paginated.Paginated<InquiryForList.InquiryForList>> {
    return Paginated.paginatedDecoder(InquiryForList.decoder())
}

export function init(
    inquiriesForList: Paginated.Paginated<InquiryForList.InquiryForList>,
    pageSection: PageSection.M,
): InquiryOverviewState {
    return {
        inquiriesForList:
            Pagination.fromPaginated(pageSection, inquiriesForList),
        jumpToPopoverOpen: false,
        jumpToInput: '',
        jumpToValidation: null,
        jumpToSubmissionAttempted: false,
        jumpToLoading: false,
        jumpToError: null,
    }
}

type InquiryOverviewLoop =
    FeedbackLoop<InquiryOverviewState, InquiryOverviewFeedback>

export function reduce(
    session: Session,
    state: InquiryOverviewState,
    action: InquiryOverviewAction,
): InquiryOverviewLoop {
    switch (action.type) {
        case 'NoOp':
            return {state, feedback: []}
        case 'MenuButtonClicked':
            return {state, feedback: [{type: 'OpenDrawer'}]}
        case 'PaginationNavigation':
            return requestInquiries(
                session,
                {
                    state: update(
                        {
                            inquiriesForList:
                                Pagination.navigate(
                                    action.navigation,
                                    state.inquiriesForList,
                                )
                        },
                        state,
                    ),
                    feedback: []
                }
            )
        case 'ItemsPerPageChanged':
            return requestInquiries(
                session,
                {
                    state: update(
                        {
                            inquiriesForList:
                                Pagination.setItemsPerPage(
                                    action.value,
                                    state.inquiriesForList,
                                )
                        },
                        state,
                    ),
                    feedback: []
                }
            )
        case 'ToggleJumpTo':
            return {
                state: update({jumpToPopoverOpen: action.open}, state),
                feedback: [],
            }
        case 'JumpToInputChanged':
            return {
                state:
                    update(
                        {
                            jumpToInput: action.value,
                            jumpToValidation:
                                validateInputField(
                                    jumpToValidator,
                                    state.jumpToSubmissionAttempted,
                                    action.value,
                                ),
                        },
                        state
                    ),
                feedback: [],
            }
        case 'JumpToSubmit':
            const validationResult =
                jumpToValidator(state.jumpToInput)
            return ((): InquiryOverviewLoop => {
                switch (validationResult.type) {
                    case 'Ok':
                        const inquiryNumber = validationResult.value
                        return {
                            state:
                                update(
                                    {
                                        jumpToLoading: true,
                                        jumpToValidation: null,
                                        jumpToSubmissionAttempted: false,
                                    },
                                    state,
                                ),
                            feedback: [{
                                type: 'SendRequest',
                                identifier: {
                                    type: 'JumpToCheck',
                                    inquiryNumber,
                                },
                                httpRequest: InquiryResource.head(
                                    session.manufacturer.id,
                                    inquiryNumber,
                                ),
                            }],
                        }
                    case 'Err':
                        return {
                            state:
                                update(
                                    {
                                        jumpToValidation:
                                        validationResult.error,
                                        jumpToSubmissionAttempted: true,
                                        jumpToError: null,
                                    },
                                    state,
                                ),
                            feedback: [],
                        }
                }
            })()
        case 'JumpToCheckResponse':
            return ((): InquiryOverviewLoop => {
                switch (action.result.type) {
                    case 'Ok':
                        return {
                            state:
                                update(
                                    {
                                        jumpToLoading: false,
                                        jumpToError: null,
                                        jumpToInput: '',
                                        jumpToPopoverOpen: false,
                                    },
                                    state
                                ),
                            feedback: [{
                                type: 'RunEffect',
                                effect:
                                    navigate<InquiryOverviewAction>(
                                        `/inquiries/${action.inquiryNumber}`,
                                        {type: 'NoOp'}
                                    )
                            }]
                        }
                    case 'Err':
                        return {
                            state:
                                update(
                                    {
                                        jumpToLoading: false,
                                        jumpToError: action.result.error,
                                    },
                                    state,
                                ),
                            feedback: [],
                        }
                }
            })()
    }
}

const jumpToValidator =
    Result.compose(stringToIntValidator(), mandatoryStringValidator())

function requestInquiries(
    session: Session,
    {state, feedback}: InquiryOverviewLoop
): InquiryOverviewLoop {
    return {
        state,
        feedback: R.append<InquiryOverviewFeedback>(
            {
                type: 'RunEffect',
                effect:
                    navigate<InquiryOverviewAction>(
                        buildUrl(
                            '/inquiries',
                            PageSection.toQueryParameters(
                                state.inquiriesForList.pageSection,
                            ),
                        ),
                        {type: 'NoOp'}
                    )
            },
            feedback,
        ),
    }
}

export function httpResponseAction(
    identifier: InquiryOverviewRequestIdentifier,
    result: Result.Result<HttpError, string>
): InquiryOverviewAction {
    switch (identifier.type) {
        case 'JumpToCheck':
            return {
                type: 'JumpToCheckResponse',
                inquiryNumber: identifier.inquiryNumber,
                result,
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/InquiryOverview/InquiryOverviewReducer.ts